export type ParsedToolCallParamBindingRef = {
  readonly sourceName: string | undefined;
  readonly pointer: string;
};

/**
 * Upstream refs are keyed by the source node's stable definition id
 * (`{{nodeId/pointer}}`). The workflow trigger has no graph node — its payload
 * is exposed under this shared sentinel id so entry-node refs to the trigger
 * resolve without depending on a (renameable) display name.
 */
export const WORKFLOW_TRIGGER_UPSTREAM_SOURCE_ID = '__trigger__';

/**
 * Parses `sourceName/pointer` bindings. Legacy bare JSON Pointers (starting
 * with `/`) are accepted for single-upstream workflows.
 */
export function parseToolCallParamBindingRef(
  ref: string
): ParsedToolCallParamBindingRef {
  if (ref.startsWith('/')) {
    return { sourceName: undefined, pointer: ref };
  }

  const slashIndex = ref.indexOf('/');
  if (slashIndex <= 0) {
    throw new Error(
      `Invalid tool-call param binding "${ref}": expected "nodeName/pointer"`
    );
  }

  const sourceName = ref.slice(0, slashIndex);
  const pointerSegment = ref.slice(slashIndex + 1);
  const pointer = pointerSegment === '' ? '' : `/${pointerSegment}`;
  return { sourceName, pointer };
}

/** Formats a node-scoped binding ref without a leading slash on the pointer segment. */
export function formatToolCallParamBindingRef(
  sourceName: string,
  pointer: string
): string {
  const trimmedSource = sourceName.trim();
  if (trimmedSource === '') {
    throw new Error('sourceName is required for tool-call param bindings');
  }
  const normalizedPointer = pointer.startsWith('/')
    ? pointer.slice(1)
    : pointer;
  return `${trimmedSource}/${normalizedPointer}`;
}

/**
 * Template tokens embed a binding ref inside a larger string param, e.g.
 * `New PR: {{Webhook/pull_request/title}}`. The ref inside the braces uses the
 * same `sourceName/pointer` format as {@link formatToolCallParamBindingRef}.
 */
const TEMPLATE_TOKEN_SOURCE = '\\{\\{\\s*([^{}]+?)\\s*\\}\\}';

/** Wraps a binding ref as a `{{ref}}` token for embedding in a string param. */
export function formatTemplateToken(ref: string): string {
  return `{{${ref}}}`;
}

/** True when the string contains at least one `{{ref}}` template token. */
export function containsTemplateRef(text: string): boolean {
  return new RegExp(TEMPLATE_TOKEN_SOURCE).test(text);
}

function stringifyTemplateValue(value: unknown): string {
  if (value === undefined || value === null) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

/**
 * Replaces every `{{ref}}` token in `text` with `resolve(ref)`. Unresolved refs
 * (undefined/null) render as an empty string; non-string values are stringified.
 */
export function interpolateTemplateRefs(
  text: string,
  resolve: (ref: string) => unknown
): string {
  return text.replace(
    new RegExp(TEMPLATE_TOKEN_SOURCE, 'g'),
    (_full, ref: string) => stringifyTemplateValue(resolve(ref.trim()))
  );
}

export type TemplateSegment =
  | { readonly t: 'text'; readonly value: string }
  | { readonly t: 'ref'; readonly ref: string };

/**
 * Splits a template string into ordered literal-text and `{{ref}}` segments, so
 * a token editor can render text runs and atomic pills from a saved param.
 */
export function splitTemplate(text: string): TemplateSegment[] {
  const segments: TemplateSegment[] = [];
  const regex = new RegExp(TEMPLATE_TOKEN_SOURCE, 'g');
  let lastIndex = 0;
  let match = regex.exec(text);
  while (match !== null) {
    if (match.index > lastIndex) {
      segments.push({ t: 'text', value: text.slice(lastIndex, match.index) });
    }
    segments.push({ t: 'ref', ref: (match[1] ?? '').trim() });
    lastIndex = match.index + match[0].length;
    match = regex.exec(text);
  }
  if (lastIndex < text.length) {
    segments.push({ t: 'text', value: text.slice(lastIndex) });
  }
  return segments;
}

/** A `{{ref}}` token found inside a param value, and where it was found. */
export type TemplateRefLocation = {
  /**
   * Dotted path to the string the token sits in, relative to the value walked —
   * `rows.0.values.email` for a token inside a dataset write's row. Empty when
   * the value walked was itself a string.
   */
  readonly path: string;
  readonly ref: string;
};

const joinTemplatePath = (path: string, segment: string): string =>
  path === '' ? segment : `${path}.${segment}`;

/**
 * Every `{{ref}}` token embedded anywhere in a param value, with the path that
 * reaches it.
 *
 * The counterpart to {@link interpolateTemplateRefs}, which resolves the same
 * tokens at run time: this is how a validator finds the refs a node depends on
 * without also resolving them. Params are walked whole rather than at the top
 * level because a tool input is not flat — a dataset write's refs live inside
 * `rows[].values`, several structures down, and a check that only looked at
 * string params would pass every one of them.
 */
export function collectTemplateRefs(
  value: unknown,
  path = ''
): TemplateRefLocation[] {
  if (typeof value === 'string') {
    return splitTemplate(value)
      .filter(
        (segment): segment is { t: 'ref'; ref: string } => segment.t === 'ref'
      )
      .map((segment) => ({ path, ref: segment.ref }));
  }
  if (Array.isArray(value)) {
    return value.flatMap((item, index) =>
      collectTemplateRefs(item, joinTemplatePath(path, String(index)))
    );
  }
  if (value !== null && typeof value === 'object') {
    return Object.entries(value).flatMap(([key, item]) =>
      collectTemplateRefs(item, joinTemplatePath(path, key))
    );
  }
  return [];
}
